import { createContext, useContext, useState, useEffect } from "react";
import { doc, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";

// ═══════════════════════════════════════════════════════════════════
// SchoolContext — provides the current school scope throughout the app.
//
// The school can be loaded in two ways:
//
//   1. BY URL SLUG (S3 routing, pre-login):
//      When the URL is /school/{slug}/..., the context loads the school
//      by querying schools/ where slug == {slug}. This works WITHOUT
//      being logged in, so the login screens of vendor/parent/student
//      can already know "which school" they belong to.
//
//   2. BY USER ROLE (post-login):
//      After Firebase Auth login, we look up userRoles/{uid} which has
//      { role, schoolId } and cross-check with the slug loaded above.
//      A super-admin (role: "super_admin") has no schoolId.
//
// States:
//   - loading: true while resolving
//   - schoolId: the firestore document id of the school, or null
//   - schoolSlug: the human-readable slug (from URL), or null
//   - school: the school document data, or null
//   - role: "super_admin" | "admin" | "vendor" | "parent" | null
//   - isSuperAdmin: convenience boolean
//   - error: string if slug was not found, or school mismatch, or null
// ═══════════════════════════════════════════════════════════════════

const SchoolContext = createContext({
  schoolId: null,
  schoolSlug: null,
  school: null,
  role: null,
  loading: true,
  isSuperAdmin: false,
  error: null,
  refresh: () => {},
});

// Parse the school slug from the current URL
// Format: /school/{slug}/[vendedor|padres|estudiante|pos|...]
// Returns null if no slug in URL
function parseSlugFromUrl() {
  const path = window.location.pathname.toLowerCase();
  const match = path.match(/^\/school\/([a-z0-9_-]+)(?:\/|$)/);
  return match ? match[1] : null;
}

// Fetch a school document by its slug
async function fetchSchoolBySlug(slug) {
  const q = query(collection(db, "schools"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { ...d.data(), id: d.id };
}

export function SchoolProvider({ children }) {
  const [schoolId, setSchoolId] = useState(null);
  const [schoolSlug, setSchoolSlug] = useState(null);
  const [school, setSchool] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Track URL changes (browser back/forward)
  const [urlSlug, setUrlSlug] = useState(parseSlugFromUrl);
  useEffect(() => {
    const onPop = () => setUrlSlug(parseSlugFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      // 1. If URL has a slug, try to load that school first
      let slugSchool = null;
      if (urlSlug) {
        try {
          slugSchool = await fetchSchoolBySlug(urlSlug);
          if (cancelled) return;
          if (!slugSchool) {
            setSchoolId(null);
            setSchoolSlug(urlSlug);
            setSchool(null);
            setRole(null);
            setError("Escuela no encontrada: " + urlSlug);
            setLoading(false);
            return;
          }
          if (slugSchool.active === false) {
            setSchoolId(null);
            setSchoolSlug(urlSlug);
            setSchool(null);
            setRole(null);
            setError("Esta escuela está inactiva: " + urlSlug);
            setLoading(false);
            return;
          }
        } catch (e) {
          if (cancelled) return;
          console.error("Error loading school by slug:", e);
          setError("Error cargando la escuela");
          setLoading(false);
          return;
        }
      }

      // Set the slug-loaded school (may be null if no slug in URL)
      if (slugSchool) {
        setSchoolId(slugSchool.id);
        setSchoolSlug(slugSchool.slug);
        setSchool(slugSchool);
      } else {
        setSchoolId(null);
        setSchoolSlug(null);
        setSchool(null);
      }

      // 2. Check current auth state (may have a logged-in user already)
      //    This is handled separately by the auth listener below
    }

    load();
    return () => { cancelled = true; };
  }, [urlSlug, refreshCounter]);

  // Separate auth listener: when auth state changes, update the `role`
  // and cross-check with any slug-loaded school
  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      // Not logged in — clear the role, keep the slug school if any
      if (!user) {
        setRole(null);
        return;
      }

      // Look up userRoles/{uid}
      try {
        const roleDoc = await getDoc(doc(db, "userRoles", user.uid));
        if (cancelled) return;

        if (!roleDoc.exists()) {
          // Anonymous student or user without role — leave role null
          setRole(null);
          return;
        }

        const roleData = roleDoc.data();
        const userRole = roleData.role;

        // Super-admin: no school needed
        if (userRole === "super_admin") {
          setRole("super_admin");
          return;
        }

        // Regular user: must have schoolId
        const userSchoolId = roleData.schoolId;
        if (!userSchoolId) {
          console.warn("User has role but no schoolId:", user.uid);
          setRole(null);
          return;
        }

        // Cross-check: if a slug-school was loaded, they must match
        // (We use a closure-captured schoolId here, but it might be stale.
        // The safer approach is to fetch the current schoolId from state
        // via a ref, but for simplicity we just re-load the school from the user's role)
        const schoolDoc = await getDoc(doc(db, "schools", userSchoolId));
        if (cancelled) return;

        if (!schoolDoc.exists() || schoolDoc.data().active === false) {
          console.warn("User's school not found or inactive:", userSchoolId);
          setRole(null);
          return;
        }

        // Success — set role and fill school if not already set by slug
        setRole(userRole);
        setSchoolId(prev => prev || userSchoolId);
        setSchool(prev => prev || { ...schoolDoc.data(), id: schoolDoc.id });
        setSchoolSlug(prev => prev || schoolDoc.data().slug);
      } catch (e) {
        if (cancelled) return;
        console.error("Error loading user role:", e);
        setRole(null);
      }
    });

    return () => { cancelled = true; unsub(); };
  }, [refreshCounter]);

  // Combine loading state: we're loading until the slug-school resolution is done
  // The auth listener runs in parallel; role may arrive later but that's OK
  useEffect(() => {
    // Mark loading=false once the slug resolution is settled (whether success, error, or no slug)
    if (urlSlug && school) setLoading(false);
    else if (urlSlug && error) setLoading(false);
    else if (!urlSlug) setLoading(false);
  }, [urlSlug, school, error]);

  const refresh = () => setRefreshCounter(c => c + 1);

  const value = {
    schoolId,
    schoolSlug,
    school,
    role,
    loading,
    isSuperAdmin: role === "super_admin",
    error,
    refresh,
  };

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

// Hook for consuming the school context
export function useSchool() {
  return useContext(SchoolContext);
}

// ═══════════════════════════════════════════════════════════════════
// Helper to build paths under schools/{schoolId}/...
// Usage: getSchoolPath("abc123", "products") → "schools/abc123/products"
//        getSchoolPath("abc123", "orders", "order_xyz") → "schools/abc123/orders/order_xyz"
//
// Throws if schoolId is null/undefined to prevent silent bugs.
// ═══════════════════════════════════════════════════════════════════
export function getSchoolPath(schoolId, ...segments) {
  if (!schoolId) {
    throw new Error("getSchoolPath called without schoolId — check that SchoolContext is loaded before calling hooks that need it.");
  }
  return ["schools", schoolId, ...segments].join("/");
}

// ═══════════════════════════════════════════════════════════════════
// Router helpers — build URLs with school slug
// ═══════════════════════════════════════════════════════════════════
export function buildSchoolUrl(slug, mode) {
  if (!slug) return "/";
  const modePath = mode === "vendor" ? "vendedor"
    : mode === "student" ? "estudiante"
    : mode === "parent" ? "padres"
    : mode === "pos" ? "pos"
    : "";
  return `/school/${slug}${modePath ? "/" + modePath : ""}`;
}

export function parseModeFromUrl() {
  const path = window.location.pathname.toLowerCase();
  if (path.startsWith("/about")) return "about";
  if (path.includes("vendedor")) return "vendor";
  if (path.includes("estudiante")) return "student";
  if (path.includes("padres")) return "parent";
  if (path.includes("pos")) return "pos";
  if (path.startsWith("/admin")) return "superadmin";
  return null;
}
